// Katalog og opsætning: de regler, der gælder, når ejer/admin retter ydelser, tilvalg, zoner, rabatkoder, felter,
// indstillinger, lukkedage og steamere. Samme regler i testversionen og i den rigtige udgave, så en fejl siges ens.
// Funktionerne retter og tjekker kun – de gemmer ikke. Ingen afhængigheder uden for src/core.
import { fejl } from './fejl.ts';
import type { Arbejdstid, Formularfelt, Fravaer, Kategori, Kupon, Lukkedag, SaerligDag, Steamer, Tilvalg, Ydelse, YdelsePris, Zone } from './typer.ts';
import type { Indstillinger } from './indstillinger.ts';
import { email as tjekEmail, gyldigDato, nyKode, rens, rensTekst, telefon as tjekTelefon } from './validering.ts';
import { gyldigtPostnr } from './geo.ts';
import { FLYT_TIMER, GEBYR_FELTER, gebyrerFra } from './gebyrer.ts';
import { STANDARD_MEDLEM } from './medlemskab.ts';
import { LIVE, normLiveIndst } from './liveBeskeder.ts';
import { normBetaling } from './betaling.ts';

const kort = () => nyKode(5).toLowerCase();
const navnFejl = (navn: string) => { if (navn.length < 2) throw fejl('Skriv et navn.', { navn: 'Skriv et navn.' }); };

/** Læsbart id ud fra navnet, fx "Hele bilen" → hele_bilen_x7k. */
export function idFraNavn(navn: string): string {
  return navn.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40) + '_' + nyKode(3).toLowerCase();
}

export function normYdelse(y: Ydelse, priser: YdelsePris[]): { ydelse: Ydelse; priser: YdelsePris[] } {
  const navn = rens(y.navn, 80); navnFejl(navn);
  for (const p of priser) if (!(p.pris >= 0) || !(p.varighed >= 15) || p.varighed > 720) throw fejl('Varighed skal være 15–720 minutter, og prisen 0 eller mere.');
  if (new Set(priser.map((p) => p.biltype_id)).size !== priser.length) throw fejl('En biltype har to priser.');
  const id = y.id || idFraNavn(navn);
  const ydelse: Ydelse = { ...y, id, navn, beskrivelse: rensTekst(y.beskrivelse ?? '', 500), inkluderer: (y.inkluderer ?? []).map((x) => rens(x, 120)).filter(Boolean).slice(0, 30) };
  return { ydelse, priser: priser.map((p) => ({ ydelse_id: id, biltype_id: p.biltype_id, pris: Math.round(p.pris), varighed: Math.round(p.varighed) })) };
}

export function normTilvalg(t: Tilvalg): Tilvalg {
  const navn = rens(t.navn, 80); navnFejl(navn);
  if (!(t.max_antal >= 1)) throw fejl('Højst antal skal være mindst 1.');
  if (!(t.pris >= 0) || !(t.varighed >= 0)) throw fejl('Pris og tid skal være 0 eller mere.');
  const liste = (l?: string[]) => [...new Set((l ?? []).map((x) => rens(String(x), 60)).filter(Boolean))].slice(0, 100);
  const { ydelser: _y, biltyper: _b, ...rest } = t;
  const ydelser = liste(t.ydelser), biltyper = liste(t.biltyper);
  return { ...rest, id: t.id || 'tv_' + kort(), navn, ...(t.beskrivelse ? { beskrivelse: rens(t.beskrivelse, 200) } : {}), max_antal: t.pr_stk ? Math.round(t.max_antal) : 1,
    ...(ydelser.length ? { ydelser } : {}), ...(biltyper.length ? { biltyper } : {}) };
}

export function normKategori(k: Kategori): Kategori {
  const navn = rens(k.navn, 60); if (navn.length < 2) throw fejl('Skriv et navn.');
  return { ...k, id: k.id || 'kat_' + kort().slice(0, 4), navn };
}
export function kategoriKanSlettes(id: string, ydelser: Pick<Ydelse, 'kategori_id'>[]) {
  if (ydelser.some((y) => y.kategori_id === id)) throw fejl('Kategorien bruges af en ydelse. Flyt ydelserne først.');
}

export function normZone(z: Zone): Zone {
  const navn = rens(z.navn, 60); navnFejl(navn);
  if (!z.intervaller.length) throw fejl('Tilføj mindst ét postnummerinterval.');
  for (const [fra, til] of z.intervaller) if (!(fra >= 1000 && til <= 9999 && fra <= til)) throw fejl('Postnumre skal være 1000–9999, og "fra" før "til".');
  return { id: z.id || 'zone_' + kort().slice(0, 4), navn, intervaller: z.intervaller.map(([a, b]) => [Math.round(a), Math.round(b)] as [number, number]) };
}

/** findes: om koden allerede er i brug (kun relevant for en ny kode). */
export function normKupon(k: Kupon, ny: boolean, findes: boolean, iDag: string): Kupon {
  const kode = rens(k.kode, 30).toUpperCase().replace(/\s/g, '');
  if (!/^[A-ZÆØÅ0-9_-]{3,30}$/.test(kode)) throw fejl('Koden skal være 3–30 bogstaver eller tal.', { kode: 'Ugyldig kode.' });
  if (ny && findes) throw fejl('Koden findes allerede.', { kode: 'Findes allerede.' });
  if (k.type === 'procent' && !(k.vaerdi > 0 && k.vaerdi <= 100)) throw fejl('Procent skal være 1–100.', { vaerdi: '1–100.' });
  if (k.type === 'beloeb' && !(k.vaerdi > 0)) throw fejl('Beløbet skal være over 0.', { vaerdi: 'Over 0.' });
  if (k.gyldig_til && !gyldigDato(k.gyldig_til)) throw fejl('Ugyldig slutdato.', { gyldig_til: 'Ugyldig dato.' });
  if (k.max_brug != null && !(k.max_brug >= 1)) throw fejl('Højst antal brug skal være mindst 1 eller tomt.', { max_brug: 'Mindst 1.' });
  return { ...k, kode, navn: rens(k.navn, 80) || kode, oprettet: k.oprettet || iDag, brugt: k.brugt ?? 0 };
}

export function normFelt(f: Formularfelt): Formularfelt {
  const navn = rens(f.navn, 80); navnFejl(navn);
  if (f.type === 'valg' && f.valg.filter(Boolean).length < 2) throw fejl('Tilføj mindst to valgmuligheder.', { valg: 'Mindst to.' });
  return { ...f, id: f.id || 'felt_' + kort(), navn, valg: f.valg.map((v) => rens(v, 80)).filter(Boolean), hjaelp: rens(f.hjaelp ?? '', 200) };
}

/** Tjekker de samlede indstillinger og retter gebyrerne til. Kaster en forståelig fejl. */
export function tjekIndstillinger(ny: Indstillinger): Indstillinger {
  const b = ny.booking;
  if (!(b.trin >= 5 && b.trin <= 120) || !(b.pause >= 0 && b.pause <= 180) || !(b.maxKoersel >= 10 && b.maxKoersel <= 400) || !(b.minVarselTimer >= 0) || !(b.aflysFristTimer >= 0)) throw fejl('Tjek tallene under Booking.');
  if (b.tidsmodel === 'faste' && (!b.fasteTider.length || b.fasteTider.some((t) => !(t >= 0 && t < 1440)))) throw fejl('Tilføj mindst én fast starttid.');
  if (!(b.slotLaengde >= 15 && b.slotLaengde <= 720) || !(b.bufferFoer >= 0 && b.bufferFoer <= 120) || !(b.bufferEfter >= 0 && b.bufferEfter <= 120)) throw fejl('Tjek tidernes længde og buffer.');
  if (b.maxOpgaver != null && !(b.maxOpgaver >= 1 && b.maxOpgaver <= 20)) throw fejl('Højst antal opgaver skal være 1–20 eller tomt.');
  if (b.ruteFlyt && (typeof b.ruteFlyt.aktiv !== 'boolean' || !(b.ruteFlyt.maxTimer >= 0 && b.ruteFlyt.maxTimer <= 8) || (b.ruteFlyt.maxTimer * 2) % 1 !== 0)) throw fejl('Max flytning pr. kunde skal være 0–8 timer (hele eller halve).');
  if (!(ny.moms.procent >= 0 && ny.moms.procent <= 50)) throw fejl('Momsprocenten skal være 0–50.');
  if (!(ny.frister.fejl >= 1 && ny.frister.skade >= 1)) throw fejl('Fristerne skal være mindst 1 time.');
  if (ny.firma.email && !tjekEmail(ny.firma.email)) throw fejl('Ugyldig e-mail til firmaet.');
  if (!(ny.beskeder.stilleFra >= 0 && ny.beskeder.stilleFra <= 24 && ny.beskeder.stilleTil >= 0 && ny.beskeder.stilleTil <= 24)) throw fejl('Ugyldige stille timer.');
  if (ny.beskeder.smsAfsender.length > 11) throw fejl('SMS-afsender må højst være 11 tegn.');
  if (ny.beskeder.mailFra && !tjekEmail(ny.beskeder.mailFra)) throw fejl('Ugyldig afsenderadresse til mails.');
  const ms = ny.medlemskab ?? STANDARD_MEDLEM;
  if (!(ms.minBesoeg >= 1 && ms.minBesoeg <= 12) || Object.values(ms.rabat).some((x) => !(x >= 0 && x <= 60))) throw fejl('Medlemsrabat skal være 0–60 %, og mindst 1 besøg om året.');
  if (ms.minimum && (!(Number.isInteger(ms.minimum.maaneder) && ms.minimum.maaneder >= 1 && ms.minimum.maaneder <= 36) || !['kontor', 'ejer'].includes(ms.minimum.frafald)))
    throw fejl('Minimum: perioden skal være 1–36 måneder.');
  if (ny.steamer && !['underskrift', 'sms'].includes(ny.steamer.ekstraPaaStedet)) throw fejl('Vælg hvordan kunden godkender ekstra tilvalg på stedet.');
  const lg = ny.liveBeskeder?.faaGraense;
  if (lg != null && !(Number.isInteger(lg) && lg >= 1 && lg <= LIVE.graenseMax)) throw fejl(`Grænsen for få ledige tider skal være 1–${LIVE.graenseMax}.`);
  const g = gebyrerFra(ny.gebyrer);
  if (GEBYR_FELTER.some((k) => !(Number.isInteger(g[k]) && g[k] >= 0 && g[k] <= 100000))) throw fejl('Gebyrer skal være 0–1.000 kr.');
  const ft = (ny.gebyrer as Partial<typeof g> | undefined)?.flytTimer;
  if (ft != null && !(Number.isInteger(ft) && ft >= FLYT_TIMER.min && ft <= FLYT_TIMER.max)) throw fejl(`Grænsen for flytning skal være ${FLYT_TIMER.min}–${FLYT_TIMER.max} timer.`);
  return { ...ny, gebyrer: g, liveBeskeder: normLiveIndst(ny.liveBeskeder), betaling: normBetaling(ny.betaling) };
}

export function normLukkedag(l: Lukkedag): Lukkedag {
  if (!gyldigDato(l.dato)) throw fejl('Vælg en dato.');
  return { dato: l.dato, navn: rens(l.navn, 60) || 'Lukket' };
}

export function normSteamer(s: Steamer, tider: Arbejdstid[]): { steamer: Steamer; tider: Arbejdstid[] } {
  const navn = rens(s.navn, 100); navnFejl(navn);
  if (!gyldigtPostnr(s.start_postnr)) throw fejl('Skriv startpostnummer.', { start_postnr: 'Ugyldigt postnummer.' });
  const tlf = s.telefon ? tjekTelefon(s.telefon) : ''; if (s.telefon && !tlf) throw fejl('Ugyldigt mobilnummer.', { telefon: 'Ugyldigt nummer.' });
  for (const t of tider) if (!(t.slut > t.start) || t.start < 0 || t.slut > 1440 || !(t.ugedag >= 1 && t.ugedag <= 7)) throw fejl('Sluttid skal være efter starttid.');
  if (s.maxOpgaver != null && !(s.maxOpgaver >= 1 && s.maxOpgaver <= 20)) throw fejl('Højst antal opgaver skal være 1–20 eller tomt.');
  return { steamer: { ...s, navn, telefon: tlf || '', start_by: rens(s.start_by ?? '', 60) }, tider: tider.map((t) => ({ steamer_id: s.id, ugedag: t.ugedag, start: t.start, slut: t.slut })) };
}

export function normSaerligDag(d: Omit<SaerligDag, 'id'>): Omit<SaerligDag, 'id'> {
  if (!gyldigDato(d.dato)) throw fejl('Vælg en dato.');
  if (d.slut < d.start || d.start < 0 || d.slut > 1440) throw fejl('Sluttid skal være efter starttid.');
  return { steamer_id: d.steamer_id, dato: d.dato, start: d.start, slut: d.slut };
}

export function normFravaer(f: Omit<Fravaer, 'id'>): Omit<Fravaer, 'id'> {
  if (!gyldigDato(f.fra) || !gyldigDato(f.til) || f.til < f.fra) throw fejl('Vælg fra- og til-dato.');
  if (!['fri', 'syg', 'ferie'].includes(f.type)) throw fejl('Vælg fri, syg eller ferie.');
  return { steamer_id: f.steamer_id, fra: f.fra, til: f.til, type: f.type, note: rens(f.note ?? '', 200) };
}
